import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Badge, Alert, Modal, Form, Row, Col } from 'react-bootstrap';

const STPFormSelection = ({ onFormSelect, onClose }) => {
  const [forms, setForms] = useState([]);
  const [selectedForms, setSelectedForms] = useState([]);
  const [validationResults, setValidationResults] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const response = await fetch('/api/bas-forms');
      const data = await response.json();
      setForms(data);
    } catch (error) {
      console.error('Error fetching forms:', error);
    }
  };

  const handleFormToggle = (form) => {
    setSelectedForms(prev => {
      const isSelected = prev.some(f => f.id === form.id);
      if (isSelected) {
        return prev.filter(f => f.id !== form.id);
      } else {
        return [...prev, form];
      }
    });
  };

  const validateSelectedForms = async () => {
    if (selectedForms.length === 0) {
      alert('Please select at least one form to validate');
      return;
    }

    setIsValidating(true);
    const results = {};

    for (const form of selectedForms) {
      try {
        const response = await fetch('/api/validate-ato-enhanced', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form)
        });
        const validation = await response.json();
        results[form.id] = validation;
      } catch (error) {
        results[form.id] = {
          valid: false,
          errors: ['Validation failed: ' + error.message],
          warnings: []
        };
      }
    }

    setValidationResults(results);
    setIsValidating(false);
    setShowValidationModal(true);
  };

  const getValidationStatus = (formId) => {
    const result = validationResults[formId];
    if (!result) return 'pending';
    return result.valid ? 'valid' : 'invalid';
  };

  const getValidationBadge = (formId) => {
    const status = getValidationStatus(formId);
    const configs = {
      pending: { variant: 'secondary', text: 'Not Validated' },
      valid: { variant: 'success', text: 'Valid' },
      invalid: { variant: 'danger', text: 'Invalid' }
    };
    const config = configs[status];
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const getReadyForms = () => {
    return selectedForms.filter(form => {
      const result = validationResults[form.id];
      return result && result.valid;
    });
  };

  const handleProceed = () => {
    const readyForms = getReadyForms();
    if (readyForms.length === 0) {
      alert('No forms are ready for STP submission. Please validate and fix any issues.');
      return;
    }
    onFormSelect(readyForms);
    onClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU');
  };

  return (
    <Modal show={true} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-list-check me-2"></i>
          Select Forms for STP Submission
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          <i className="fas fa-info-circle me-2"></i>
          <strong>STP Submission Requirements:</strong>
          <ul className="mb-0 mt-2">
            <li>Forms must be completely filled out</li>
            <li>All required fields must be validated</li>
            <li>ATO compliance validation must pass</li>
            <li>Forms must be in "Ready" or "Submitted" status</li>
          </ul>
        </Alert>

        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <span>Available Forms ({forms.length})</span>
            <div>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={validateSelectedForms}
                disabled={selectedForms.length === 0 || isValidating}
                className="me-2"
              >
                {isValidating ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-1"></i>
                    Validating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check-circle me-1"></i>
                    Validate Selected
                  </>
                )}
              </Button>
              <Button
                variant="success"
                onClick={handleProceed}
                disabled={getReadyForms().length === 0}
              >
                <i className="fas fa-arrow-right me-1"></i>
                Proceed with {getReadyForms().length} Ready Forms
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th width="50px">Select</th>
                    <th>Business Name</th>
                    <th>ABN</th>
                    <th>Period</th>
                    <th>Total Amount</th>
                    <th>Status</th>
                    <th>Validation</th>
                  </tr>
                </thead>
                <tbody>
                  {forms.map((form) => (
                    <tr key={form.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedForms.some(f => f.id === form.id)}
                          onChange={() => handleFormToggle(form)}
                        />
                      </td>
                      <td>{form.business_name}</td>
                      <td>{form.abn}</td>
                      <td>
                        {formatDate(form.period_start)} - {formatDate(form.period_end)}
                      </td>
                      <td className="text-end">
                        <strong>{formatCurrency(form.total_amount)}</strong>
                      </td>
                      <td>
                        <Badge bg={form.status === 'submitted' ? 'success' : 
                                  form.status === 'ready' ? 'warning' : 'secondary'}>
                          {form.status}
                        </Badge>
                      </td>
                      <td>{getValidationBadge(form.id)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {selectedForms.length > 0 && (
          <Card>
            <Card.Header>Selected Forms Summary</Card.Header>
            <Card.Body>
              <Row>
                <Col md={3}>
                  <div className="text-center">
                    <h5 className="text-primary">{selectedForms.length}</h5>
                    <small className="text-muted">Selected</small>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <h5 className="text-success">{getReadyForms().length}</h5>
                    <small className="text-muted">Ready</small>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <h5 className="text-warning">
                      {selectedForms.length - getReadyForms().length}
                    </h5>
                    <small className="text-muted">Need Validation</small>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <h5 className="text-info">
                      {formatCurrency(selectedForms.reduce((sum, f) => sum + (parseFloat(f.total_amount) || 0), 0))}
                    </h5>
                    <small className="text-muted">Total Amount</small>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </Modal.Footer>

      {/* Validation Results Modal */}
      <Modal show={showValidationModal} onHide={() => setShowValidationModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Validation Results</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {Object.entries(validationResults).map(([formId, result]) => {
            const form = forms.find(f => f.id == formId);
            return (
              <Card key={formId} className="mb-3">
                <Card.Header>
                  <strong>{form?.business_name}</strong> - {form?.period_start} to {form?.period_end}
                </Card.Header>
                <Card.Body>
                  {result.valid ? (
                    <Alert variant="success">
                      <i className="fas fa-check-circle me-2"></i>
                      <strong>Validation Passed!</strong>
                    </Alert>
                  ) : (
                    <Alert variant="danger">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      <strong>Validation Failed!</strong>
                      {result.errors && result.errors.length > 0 && (
                        <ul className="mb-0 mt-2">
                          {result.errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      )}
                    </Alert>
                  )}
                  {result.warnings && result.warnings.length > 0 && (
                    <Alert variant="warning">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      <strong>Warnings:</strong>
                      <ul className="mb-0 mt-2">
                        {result.warnings.map((warning, index) => (
                          <li key={index}>{warning}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            );
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowValidationModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

export default STPFormSelection;
